import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Card, CardContent, Stack, Chip, CircularProgress, Avatar, LinearProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Pagination } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { API_SERVER_URL } from '../../../../config/api';
import { styled, alpha } from '@mui/material/styles';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar
} from 'recharts';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RepeatIcon from '@mui/icons-material/Repeat';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ImageIcon from '@mui/icons-material/Image';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: '32px',
  backgroundColor: '#f0f4f8',
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  '@media (max-width: 768px)': {
    padding: '20px 16px'
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '24px'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '26px',
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: '4px'
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#64748b'
}));

const MetricCard = styled(Card)(({ gradient }) => ({
  height: '100%',
  minHeight: '180px',
  borderRadius: '16px',
  border: 'none',
  background: gradient || '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '120px',
    height: '120px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(30%, -30%)'
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)'
  }
}));

const MetricIconBox = styled(Box)(({ bgcolor }) => ({
  width: '52px',
  height: '52px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bgcolor || 'rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: '24px',
  borderRadius: '16px',
  height: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.03)'
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: '4px'
}));

const ChartSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  color: '#94a3b8',
  marginBottom: '20px'
}));

const ActivityItem = styled(Box)(({ theme }) => ({
  padding: '14px 18px',
  borderRadius: '12px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    borderColor: '#cbd5e1'
  }
}));

const StatusChip = styled(Chip)(({ statuscolor }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '10px',
  height: '22px',
  borderRadius: '6px',
  backgroundColor: alpha(statuscolor, 0.1),
  color: statuscolor,
  border: `1px solid ${alpha(statuscolor, 0.2)}`
}));

const TrendBadge = styled(Box)(({ positive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: '16px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#ffffff'
}));

const FeatureBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '14px'
}));

const CHART_COLORS = ['#6366f1', '#10b981', '#2563eb', '#3b82f6', '#ec4899'];
const GRADIENT_CARDS = [
  'linear-gradient(135deg, #6366f1 0%, #2563eb 100%)',
  'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
  'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
];

const EngagementDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetailsOpen, setPostDetailsOpen] = useState(false);
  const [postDetailsLoading, setPostDetailsLoading] = useState(false);

  // Pagination state
  const [paginatedPosts, setPaginatedPosts] = useState([]);
  const [postsPage, setPostsPage] = useState(1);
  const [postsPagination, setPostsPagination] = useState(null);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsFilter, setPostsFilter] = useState('all'); // 'all', 'completed', 'pending'

  const fetchPaginatedPosts = async (page = 1, status = postsFilter) => {
    setPostsLoading(true);
    try {
      const response = await ApiService.get(`/admin/analytics/posts?page=${page}&limit=5&status=${status}`);
      if (response) {
        const result = response.data || response;
        setPaginatedPosts(result?.posts || []);
        setPostsPagination(result?.pagination || null);
        setPostsPage(page);
      }
    } catch (err) {
      logger.error('Error fetching paginated posts:', err);
      setPaginatedPosts([]);
    }
    setPostsLoading(false);
  };

  const handlePageChange = (event, newPage) => {
    fetchPaginatedPosts(newPage, postsFilter);
  };

  const handleFilterChange = (newFilter) => {
    setPostsFilter(newFilter);
    fetchPaginatedPosts(1, newFilter); // Reset to page 1 when filter changes
  };

  const handlePostClick = async (post) => {
    setPostDetailsLoading(true);
    setPostDetailsOpen(true);
    try {
      // Fetch full task details including post_content
      const response = await ApiService.get(`/admin/task/get-task/${post.id}`);
      const taskData = response.data?.task || response.task || response.data || response;
      setSelectedPost(taskData);
    } catch (err) {
      logger.error('Error fetching post details:', err);
      setSelectedPost(post); // Fall back to basic data
    }
    setPostDetailsLoading(false);
  };

  const handleClosePostDetails = () => {
    setPostDetailsOpen(false);
    setSelectedPost(null);
  };

  const getPostContent = () => {
    if (!selectedPost) return null;
    try {
      let content = selectedPost.post_content;
      if (typeof content === 'string') {
        content = JSON.parse(content);
      }
      return content;
    } catch (e) {
      return null;
    }
  };

  const getAttachments = () => {
    if (!selectedPost?.attachments) return [];
    try {
      let attachments = selectedPost.attachments;
      if (typeof attachments === 'string') {
        attachments = JSON.parse(attachments);
      }
      return Array.isArray(attachments) ? attachments : [];
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    fetchData();
    fetchPaginatedPosts(1);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get('/admin/analytics/engagement');
      if (response) {
        // ApiService might return the raw response or the data property depending on implementation
        // Adjusting based on typical pattern: response.data.data or response.data
        const result = response.data || response;
        setData(result);
      }
      setLoading(false);
    } catch (err) {
      logger.error('Error fetching engagement analytics:', err);
      setError('Failed to load engagement data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardContainer display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer display="flex" justifyContent="center" alignItems="center">
        <Typography color="error" variant="h6">{error}</Typography>
      </DashboardContainer>
    );
  }

  const { engagementMetrics, activityData, featureUsageData, recentActivities, postCreation } = data || {};

  if (!engagementMetrics) return null;

  const isPositiveTrend = (trend) => trend?.startsWith('+');

  const getActivityIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981' }} />;
      case 'error': return <ErrorIcon sx={{ fontSize: 20, color: '#ef4444' }} />;
      default: return <InfoIcon sx={{ fontSize: 20, color: '#6366f1' }} />;
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#6366f1';
    }
  };

  const totalFeatureUsage = featureUsageData?.reduce((sum, item) => sum + item.value, 0) || 1;

  return (
    <DashboardContainer>
      <PageHeader>
        <PageTitle>Engagement Analytics</PageTitle>
        <PageSubtitle>Track user activity, retention, and feature usage metrics</PageSubtitle>
      </PageHeader>

      {/* Key Metrics Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard gradient={GRADIENT_CARDS[0]}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <MetricIconBox>
                  <PeopleAltIcon sx={{ fontSize: 26, color: '#fff' }} />
                </MetricIconBox>
                <TrendBadge positive={isPositiveTrend(engagementMetrics.activeUsers.trend)}>
                  {isPositiveTrend(engagementMetrics.activeUsers.trend) ? (
                    <TrendingUpIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 14 }} />
                  )}
                  {engagementMetrics.activeUsers.trend}
                </TrendBadge>
              </Stack>
              <Box mt="auto" pt={3}>
                <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  {engagementMetrics.activeUsers.daily.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                  Daily Active Users
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>
                  {engagementMetrics.activeUsers.monthly.toLocaleString()} monthly users
                </Typography>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard gradient={GRADIENT_CARDS[1]}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <MetricIconBox>
                  <AccessTimeIcon sx={{ fontSize: 26, color: '#fff' }} />
                </MetricIconBox>
              </Stack>
              <Box mt="auto" pt={3}>
                <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  {engagementMetrics.avgSessionDuration}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                  Avg. Session Duration
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>
                  Based on active sessions
                </Typography>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard gradient={GRADIENT_CARDS[2]}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <MetricIconBox>
                  <RepeatIcon sx={{ fontSize: 26, color: '#fff' }} />
                </MetricIconBox>
              </Stack>
              <Box mt="auto" pt={3}>
                <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  {engagementMetrics.retentionRate}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                  Retention Rate
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>
                  Weekly / Monthly ratio
                </Typography>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard gradient={GRADIENT_CARDS[3]}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <MetricIconBox>
                  <ExitToAppIcon sx={{ fontSize: 26, color: '#fff' }} />
                </MetricIconBox>
              </Stack>
              <Box mt="auto" pt={3}>
                <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  {engagementMetrics.bounceRate}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                  Bounce Rate
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>
                  Single activity sessions
                </Typography>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} lg={8}>
          <ChartCard>
            <ChartTitle>User Activity Trends</ChartTitle>
            <ChartSubtitle>Active users and sessions over time</ChartSubtitle>
            <Box height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={activityData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      padding: '12px 16px'
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    name="Active Users"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorUsers)"
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    name="Sessions"
                    stroke="#10b981"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorSessions)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard>
            <ChartTitle>Feature Usage</ChartTitle>
            <ChartSubtitle>Distribution of feature interactions</ChartSubtitle>
            <Box height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={featureUsageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {featureUsageData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }}
                    formatter={(value, name) => [`${value} (${Math.round(value / totalFeatureUsage * 100)}%)`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              {featureUsageData?.slice(0, 5).map((feature, index) => (
                <Stack key={feature.name} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: CHART_COLORS[index] }} />
                    <Typography sx={{ fontSize: 13, color: '#374151' }}>{feature.name}</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{feature.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Feature Usage Progress Section */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <ChartTitle>Feature Engagement</ChartTitle>
            <ChartSubtitle>Breakdown of feature usage by category</ChartSubtitle>
            <Box mt={2}>
              {featureUsageData?.map((feature, index) => (
                <FeatureBar key={feature.name}>
                  <Box sx={{ minWidth: 130 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>
                      {feature.name}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(feature.value / totalFeatureUsage) * 100}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                        }
                      }}
                    />
                  </Box>
                  <Typography sx={{ minWidth: 45, fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>
                    {Math.round((feature.value / totalFeatureUsage) * 100)}%
                  </Typography>
                </FeatureBar>
              ))}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard>
            <ChartTitle>Sessions by Day</ChartTitle>
            <ChartSubtitle>Daily session distribution</ChartSubtitle>
            <Box height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }}
                  />
                  <Bar
                    dataKey="sessions"
                    name="Sessions"
                    fill="#0b91d6"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Post Creation Section */}
      <Grid container spacing={3} mb={4}>
        {/* Post Creation Stats */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <Stack direction="row" alignItems="center" gap={2} mb={3}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PostAddIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <ChartTitle>Post Creation</ChartTitle>
                <ChartSubtitle sx={{ mb: 0 }}>Posts & Photos created by admin</ChartSubtitle>
              </Box>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 2, borderRadius: '12px', bgcolor: '#dbeafe' }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>
                    {postCreation?.total || 0}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#1e40af', fontWeight: 500 }}>Total</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 2, borderRadius: '12px', bgcolor: '#dcfce7' }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#16a34a' }}>
                    {postCreation?.completed || 0}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#166534', fontWeight: 500 }}>Completed</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 2, borderRadius: '12px', bgcolor: '#dbeafe' }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#2563eb' }}>
                    {postCreation?.pending || 0}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#1e40af', fontWeight: 500 }}>Pending</Typography>
                </Box>
              </Grid>
            </Grid>
          </ChartCard>
        </Grid>

        {/* Post Creation Trend Chart */}
        <Grid item xs={12} md={8}>
          <ChartCard>
            <ChartTitle>Post Creation Trend</ChartTitle>
            <ChartSubtitle>Posts created and completed over the last 7 days</ChartSubtitle>
            <Box height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={postCreation?.trend || []} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { weekday: 'short' });
                    }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: '10px' }} />
                  <Bar
                    dataKey="created"
                    name="Created"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="completed"
                    name="Completed"
                    fill="#16a34a"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>

        {/* Recent Posts List */}
        <Grid item xs={12}>
          <ChartCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <ChartTitle>Post Tasks</ChartTitle>
                <ChartSubtitle sx={{ mb: 0 }}>All post/photo tasks created by admin</ChartSubtitle>
              </Box>
              <Chip
                label={`${postsPagination?.totalCount || postCreation?.total || 0} ${postsFilter === 'all' ? 'total' : postsFilter} posts`}
                size="small"
                sx={{
                  backgroundColor: '#eff6ff',
                  color: '#2563eb',
                  fontWeight: 600,
                  fontSize: 12
                }}
              />
            </Stack>
            {/* Filter Buttons */}
            <Stack direction="row" spacing={1} mb={3}>
              <Button
                variant={postsFilter === 'all' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleFilterChange('all')}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  backgroundColor: postsFilter === 'all' ? '#3b82f6' : 'transparent',
                  borderColor: '#3b82f6',
                  color: postsFilter === 'all' ? 'white' : '#3b82f6',
                  '&:hover': {
                    backgroundColor: postsFilter === 'all' ? '#2563eb' : 'rgba(249, 115, 22, 0.1)',
                    borderColor: '#3b82f6'
                  }
                }}
              >
                All ({postCreation?.total || 0})
              </Button>
              <Button
                variant={postsFilter === 'completed' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleFilterChange('completed')}
                startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  backgroundColor: postsFilter === 'completed' ? '#16a34a' : 'transparent',
                  borderColor: '#16a34a',
                  color: postsFilter === 'completed' ? 'white' : '#16a34a',
                  '&:hover': {
                    backgroundColor: postsFilter === 'completed' ? '#15803d' : 'rgba(22, 163, 74, 0.1)',
                    borderColor: '#16a34a'
                  }
                }}
              >
                Completed ({postCreation?.completed || 0})
              </Button>
              <Button
                variant={postsFilter === 'pending' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleFilterChange('pending')}
                startIcon={<PendingActionsIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  backgroundColor: postsFilter === 'pending' ? '#2563eb' : 'transparent',
                  borderColor: '#2563eb',
                  color: postsFilter === 'pending' ? 'white' : '#2563eb',
                  '&:hover': {
                    backgroundColor: postsFilter === 'pending' ? '#1d4ed8' : 'rgba(37, 99, 235, 0.1)',
                    borderColor: '#2563eb'
                  }
                }}
              >
                Pending ({postCreation?.pending || 0})
              </Button>
            </Stack>
            {postsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
              </Box>
            ) : paginatedPosts && paginatedPosts.length > 0 ? (
              <>
                <Stack spacing={1.5}>
                  {paginatedPosts.map((post) => (
                    <ActivityItem
                      key={post.id}
                      onClick={() => handlePostClick(post)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#3b82f6' } }}
                    >
                      <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.12), width: 40, height: 40 }}>
                        <ImageIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                          {post.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                          {post.profileName || 'No profile assigned'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 12 }} /> Created by: {post.createdBy || 'Admin'}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" gap={1}>
                        <StatusChip
                          label={post.status?.toUpperCase() || 'PENDING'}
                          size="small"
                          statuscolor={post.status === 'completed' ? '#16a34a' : '#3b82f6'}
                        />
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                          {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Stack>
                      <OpenInNewIcon sx={{ fontSize: 16, color: '#94a3b8', ml: 1 }} />
                    </ActivityItem>
                  ))}
                </Stack>
                {/* Pagination Controls */}
                {postsPagination && postsPagination.totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                        Showing {((postsPage - 1) * 5) + 1}-{Math.min(postsPage * 5, postsPagination.totalCount)} of {postsPagination.totalCount}
                      </Typography>
                      <Pagination
                        count={postsPagination.totalPages}
                        page={postsPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontWeight: 500,
                            '&.Mui-selected': {
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#2563eb'
                              }
                            }
                          }
                        }}
                      />
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <Box py={5} textAlign="center">
                <PostAddIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1.5 }} />
                <Typography sx={{ color: '#64748b', fontSize: 14 }}>No post tasks found</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 0.5 }}>
                  Create post/photo tasks from the admin panel to see them here
                </Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChartCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <ChartTitle>Recent User Activities</ChartTitle>
                <ChartSubtitle sx={{ mb: 0 }}>Latest actions performed by users</ChartSubtitle>
              </Box>
              <Chip
                label={`${recentActivities?.length || 0} activities`}
                size="small"
                sx={{
                  backgroundColor: '#eff6ff',
                  color: '#3b82f6',
                  fontWeight: 600,
                  fontSize: 12
                }}
              />
            </Stack>
            {recentActivities && recentActivities.length > 0 ? (
              <Stack spacing={1.5}>
                {recentActivities.map((activity) => (
                  <ActivityItem key={activity.id}>
                    <Avatar sx={{ bgcolor: alpha(getStatusColor(activity.type), 0.12), width: 40, height: 40 }}>
                      {getActivityIcon(activity.type)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                        {activity.user}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                        {activity.action}
                      </Typography>
                    </Box>
                    <Stack alignItems="flex-end" gap={1}>
                      <StatusChip
                        label={activity.type.toUpperCase()}
                        size="small"
                        statuscolor={getStatusColor(activity.type)}
                      />
                      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                        {activity.time}
                      </Typography>
                    </Stack>
                  </ActivityItem>
                ))}
              </Stack>
            ) : (
              <Box py={5} textAlign="center">
                <TouchAppIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1.5 }} />
                <Typography sx={{ color: '#64748b', fontSize: 14 }}>No recent activities found</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Post Details Modal */}
      <Dialog
        open={postDetailsOpen}
        onClose={handleClosePostDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PostAddIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                Post Details
              </Typography>
              <Typography sx={{ fontSize: '13px', opacity: 0.9 }}>
                View post content and images
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClosePostDetails} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {postDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
          ) : selectedPost ? (
            <Box>
              {/* Task Info */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: '12px' }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1e293b', mb: 1 }}>
                  {selectedPost.title}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                    label={selectedPost.created_at || selectedPost.createdAt ?
                      `Created: ${new Date(selectedPost.created_at || selectedPost.createdAt).toLocaleDateString()}` :
                      'No date'
                    }
                    size="small"
                    sx={{ backgroundColor: '#e2e8f0' }}
                  />
                  <Chip
                    label={selectedPost.status?.toUpperCase() || 'PENDING'}
                    size="small"
                    sx={{
                      backgroundColor: selectedPost.status === 'completed' ? '#dcfce7' : '#dbeafe',
                      color: selectedPost.status === 'completed' ? '#16a34a' : '#3b82f6',
                      fontWeight: 600
                    }}
                  />
                  {selectedPost.post_status && (
                    <Chip
                      label={`Post: ${selectedPost.post_status.toUpperCase()}`}
                      size="small"
                      sx={{
                        backgroundColor: selectedPost.post_status === 'posted' ? '#dbeafe' : '#dbeafe',
                        color: selectedPost.post_status === 'posted' ? '#2563eb' : '#3b82f6',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                  {selectedPost.assigned_to_profile_name && (
                    <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                      Profile: {selectedPost.assigned_to_profile_name}
                    </Typography>
                  )}
                  {(selectedPost.createdBy || selectedPost.creator?.full_name) && (
                    <Typography sx={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14 }} /> Created by: {selectedPost.createdBy || selectedPost.creator?.full_name || 'Admin'}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Post Content */}
              {(() => {
                const postContent = getPostContent();
                if (postContent) {
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', mb: 2 }}>
                        Post Content
                      </Typography>

                      {/* Post Text/Summary */}
                      {(postContent.summary || postContent.text || postContent.content) && (
                        <Box sx={{ p: 2, backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #fed7aa', mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                            {postContent.summary || postContent.text || postContent.content}
                          </Typography>
                          {postContent.topicType && (
                            <Chip
                              label={postContent.topicType.replace('_', ' ')}
                              size="small"
                              sx={{ mt: 1, backgroundColor: '#2563eb', color: 'white', fontSize: '11px' }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Post Images from media array */}
                      {postContent.media && Array.isArray(postContent.media) && postContent.media.length > 0 && (
                        <Box>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748b', mb: 1 }}>
                            Images ({postContent.media.length})
                          </Typography>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: postContent.media.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                            gap: 2
                          }}>
                            {postContent.media.map((mediaItem, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  position: 'relative',
                                  paddingTop: postContent.media.length === 1 ? '56.25%' : '100%',
                                  backgroundColor: '#e2e8f0',
                                  cursor: 'pointer',
                                  border: '2px solid #fed7aa',
                                  '&:hover': {
                                    borderColor: '#3b82f6'
                                  }
                                }}
                                onClick={() => {
                                  const imgUrl = mediaItem.url || mediaItem.sourceUrl || mediaItem;
                                  if (imgUrl) window.open(imgUrl, '_blank');
                                }}
                              >
                                <img
                                  src={mediaItem.url || mediaItem.sourceUrl || mediaItem}
                                  alt={`Post media ${idx + 1}`}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                                  }}
                                />
                                <Box sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(0,0,0,0.6)',
                                  borderRadius: '6px',
                                  p: 0.5
                                }}>
                                  <OpenInNewIcon sx={{ fontSize: 16, color: 'white' }} />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Single image fallback */}
                      {!postContent.media?.length && (postContent.imageUrl || postContent.mediaUrl || postContent.image) && (
                        <Box
                          sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: '2px solid #fed7aa',
                            '&:hover': { borderColor: '#3b82f6' }
                          }}
                          onClick={() => window.open(postContent.imageUrl || postContent.mediaUrl || postContent.image, '_blank')}
                        >
                          <img
                            src={postContent.imageUrl || postContent.mediaUrl || postContent.image}
                            alt="Post"
                            style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Box>
                      )}

                      {/* Scheduled Time */}
                      {(postContent.scheduledFor || postContent.scheduledTime) && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#dbeafe', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <ScheduleIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                            <Typography sx={{ fontSize: '13px', color: '#1e40af', fontWeight: 500 }}>
                              Scheduled for: {new Date(postContent.scheduledFor || postContent.scheduledTime).toLocaleString()}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                }
                return null;
              })()}

              {/* Attachments */}
              {(() => {
                const attachments = getAttachments();
                if (attachments.length > 0) {
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', mb: 2 }}>
                        Attached Files ({attachments.length})
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                        {attachments.map((attachment, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              position: 'relative',
                              paddingTop: '100%',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              backgroundColor: '#e2e8f0',
                              cursor: 'pointer',
                              border: '2px solid #bfdbfe',
                              '&:hover': {
                                borderColor: '#2563eb'
                              }
                            }}
                            onClick={() => window.open(`${API_SERVER_URL}${attachment}`, '_blank')}
                          >
                            <img
                              src={`${API_SERVER_URL}${attachment}`}
                              alt={`Attachment ${idx + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>';
                              }}
                            />
                            <Box sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              borderRadius: '6px',
                              p: 0.5
                            }}>
                              <OpenInNewIcon sx={{ fontSize: 16, color: 'white' }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}

              {/* No Content Message */}
              {!getPostContent() && getAttachments().length === 0 && (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <ImageIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
                  <Typography sx={{ color: '#64748b', fontSize: 14 }}>
                    No images or content available for this post
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ color: '#64748b' }}>No post selected</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleClosePostDetails}
            variant="contained"
            sx={{
              backgroundColor: '#3b82f6',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#2563eb'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
};

export default EngagementDashboard;
